import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import pagesService from '../services/pagesService';
import {Page} from '../types/Page';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {getValueWithFallback} from "../../../common/types/StringTranslations";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import {useToast} from "../../../common/contexts/ToastContext";

const PagesList: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [pages, setPages] = useState<Page[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    alias: {value: null, matchMode: FilterMatchMode.CONTAINS},
    title: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async (): Promise<void> => {
    const pagesData = await pagesService.getAllPages();
    setPages(pagesData);
  };

  const handleDelete = async (page: Page): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: page.alias}),
      accept: async () => {
        await handleError(
          async () => {
            await pagesService.deletePage(page.id);
            await fetchPages();
          },
          t('admin.pages.errors.deleting')
        );
      }
    });
  };

  const handleEdit = (page: Page) => {
    navigate(`${page.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const getTranslatedTitle = (page: Page): string => {
    return getValueWithFallback(page.title, i18n.language);
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.pages.title.list')}
        subtitle={t('admin.pages.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.pages.actions.createNew')}
      />

      <DataTable
        value={pages}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="alias"
          header={t('admin.common.columns.alias')}
          sortable
          filter
          filterField="alias"
          filterPlaceholder={t('admin.common.filters.searchByAlias')}
          showFilterMenu={false}
          showClearButton={false}
          body={(page: Page) => (
            <strong>{page.alias}</strong>
          )}/>
        <Column
          header={t('admin.pages.columns.title')}
          sortable
          filter
          filterField="title"
          filterPlaceholder={t('admin.pages.filters.searchByTitle')}
          showFilterMenu={false}
          showClearButton={false}
          body={(page: Page) => (
            <span>{getTranslatedTitle(page)}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(page: Page) => (
          <div>
            <Button
              onClick={() => handleEdit(page)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
            />
            <Button
              onClick={() => handleDelete(page)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              className="ml-2"
            />
          </div>
        )}/>
      </DataTable>
    </>
  );
};

export default PagesList;

